import type { Disposable, Mutable } from "@goldenratio/core-utils";

export interface Ticker {
  /**
   * Callback invoked on every frame update
   */
  on_tick(fn: TickerCallbackType): void;
  /**
   * Sets speed of the ticker.
   * Value between 0 to 1
   */
  set_speed(value: number): void;
  /**
   * Returns current fps of the ticker
   */
  get_fps(): number;
}

export interface TickerData {
  /**
   * milliseconds elapsed from last updated.
   * Ideally value should be 16.66 ms
   */
  readonly elapsed_ms: number;

  /**
   * last updated `performance.now()`
   */
  readonly last_time: number;

  /**
   * Scalar representing the delta time factor.
   * This is a dimensionless value representing the fraction of a frame at the target framerate.
   * At 60 FPS, this value is typically around 1.0.
   *
   * This is NOT in milliseconds - it's a scalar multiplier for frame-independent animations.
   */
  readonly delta_time: number;
}

/**
 * Callback for ticker updates.
 * @param ticker_data
 */
export type TickerCallbackType = (ticker_data: TickerData) => void;

export class BrowserTicker implements Ticker, Disposable {
  // Target frames per millisecond
  private readonly target_fpms: number;
  private readonly max_elapsed_ms = 100;

  private speed: number = 1;
  private last_time: number = -1;

  /**
   * Scalar representing the delta time factor.
   * This is a dimensionless value representing the fraction of a frame at the target framerate.
   * At 60 FPS, this value is typically around 1.0.
   * This is NOT in milliseconds
   * It's a scalar multiplier for frame-independent animations
   */
  private delta_time: number = 1;
  private elapsed_ms: number = 1;

  private raf_id: number = -1;
  private current_ticker_data: Mutable<TickerData> = {
    delta_time: 0,
    last_time: 0,
    elapsed_ms: 0,
  };

  constructor(fps: number = 60) {
    this.target_fpms = fps / 1000;
    this.elapsed_ms = 1 / this.target_fpms;
  }

  /**
   * @inheritdoc
   */
  on_tick(fn: TickerCallbackType): void {
    if (this.raf_id !== -1) {
      // ticker already running
      throw new Error("Ticker already running");
    }

    const update_loop = (current_time: number = performance.now()) => {
      let elapsed_ms: number;
      if (current_time > this.last_time) {
        elapsed_ms = current_time - this.last_time;
        this.elapsed_ms = elapsed_ms;
        if (elapsed_ms > this.max_elapsed_ms) {
          elapsed_ms = this.max_elapsed_ms;
        }
        this.delta_time = elapsed_ms * this.target_fpms * this.speed;
      } else {
        this.delta_time = 0;
      }
      this.last_time = current_time;

      this.current_ticker_data.delta_time = this.delta_time;
      this.current_ticker_data.elapsed_ms = this.elapsed_ms;
      this.current_ticker_data.last_time = this.last_time;
      fn(this.current_ticker_data);

      this.raf_id = globalThis.requestAnimationFrame(update_loop);
    };

    this.raf_id = globalThis.requestAnimationFrame(update_loop);
  }

  /**
   * @inheritdoc
   */
  set_speed(value: number): void {
    this.speed = value;
  }

  /**
   * @inheritdoc
   */
  get_fps(): number {
    return (1000 / this.elapsed_ms) | 0;
  }

  dispose(): void {
    if (this.raf_id !== -1) {
      globalThis.cancelAnimationFrame(this.raf_id);
      this.raf_id = -1;
    }
    this.last_time = -1;
  }
}
